/**
 * Product analytics behind one small API so components never import a vendor SDK.
 * Everything is a no-op when the provider is not configured (dev, CI, e2e).
 *
 *   track("post_created", { published: true })   custom events
 *   identify(user) / reset()                      tie events to the signed-in user (PostHog only)
 */
import { env } from "@/lib/env";

export type EventProps = Record<string, string | number | boolean | null | undefined>;

const state = { started: false };

/** Call once at startup (main.tsx). */
export function initAnalytics(): void {
  if (state.started) return;
  state.started = true;
  loadPlausible();
}

export function track(event: string, props?: EventProps): void {
  window.plausible?.(event, props ? { props } : undefined);
}

export function identify(user: { id: string; email?: string; name?: string }): void {
  void user;
}

export function reset(): void {}

declare global {
  interface Window {
    plausible?: (event: string, options?: { props?: EventProps }) => void;
  }
}

/** Injects Plausible's script through the same-origin proxy (`/pl`, see vite.config.ts). */
export function loadPlausible(doc: Document = document): void {
  if (!env.plausibleDomain || doc.querySelector("script[data-plausible]")) return;
  const w = doc.defaultView ?? window;
  w.plausible =
    w.plausible ||
    function queue(...args) {
      const q = ((w.plausible as unknown as { q?: unknown[] }).q ??= []);
      q.push(args);
    };
  const script = doc.createElement("script");
  script.defer = true;
  script.dataset.plausible = "";
  script.dataset.domain = env.plausibleDomain;
  script.dataset.api = `${env.plausibleHost}/api/event`;
  script.src = `${env.plausibleHost}/js/script.js`;
  doc.head.appendChild(script);
}

/** Test hook: forget that init ran. */
export function resetAnalyticsForTests(): void {
  state.started = false;
}
